import {
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { X, ExternalLink, Maximize2, Minus, Plus, LayoutGrid, Check } from '../../../icons';
import {
  CHAT_PREVIEW_COLS_OPTIONS,
  useChatPreviewStore,
  useEffectivePreviewZoom,
} from '../../../store/chatPreviewStore';
import { PREVIEW_ZOOM_MAX, PREVIEW_ZOOM_MIN } from '../../../lib/preview-fit';
import { PreviewThumbRail } from './preview/PreviewThumbRail';
import { fetchMarkdownText } from '../../../lib/markdown-doc';
import { fetchAttachmentText, type PreviewDoc } from '../../../lib/preview-doc';
import { previewKindIcon, previewKindClass } from '../../../lib/file-icon';
import { renderMessageMarkdown } from '../../../lib/chat-markdown';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

// 포맷별 렌더러는 lazy chunk — 해당 포맷을 처음 열 때만 라이브러리(pdfjs/docx-preview/xlsx/pptx) 다운로드.
const PdfPreview = lazy(() => import('./preview/PdfPreview'));
const DocxPreview = lazy(() => import('./preview/DocxPreview'));
const XlsxPreview = lazy(() => import('./preview/XlsxPreview'));
const PptxPreview = lazy(() => import('./preview/PptxPreview'));
// chat-hwp-preview (2026-08-14) — 한글 문서는 **읽기 전용 경량 렌더러**(234KB). 편집기 셸이
// 아니라서 호스트를 오염시키지 않는다 — 그래서 채팅에 올릴 수 있다.
const HwpPreview = lazy(() => import('./preview/HwpPreview'));

/** PreviewKind 를 빠짐없이 처리했는지 컴파일 타임에 강제 (chat-pptx-preview — 2026-07-09). */
function assertNever(kind: never): never {
  throw new Error(`unhandled preview kind: ${String(kind)}`);
}

/**
 * kind → 렌더러. 삼항 체인은 미처리 kind 를 조용히 마지막 분기로 흘려보내(빌드는 초록, 화면은 오동작)
 * 새 포맷 추가 시 함정이 된다. switch + assertNever 로 누락을 컴파일 에러로 만든다.
 */
function OfficeContent({ doc }: { doc: PreviewDoc }) {
  switch (doc.kind) {
    case 'pdf':
      return <PdfPreview doc={doc} />;
    case 'docx':
      return <DocxPreview doc={doc} />;
    case 'xlsx':
      return <XlsxPreview doc={doc} />;
    case 'pptx':
      return <PptxPreview doc={doc} />;
    case 'hwp':
      return <HwpPreview doc={doc} />;
    case 'markdown':
    case 'html':
      return null; // 호출부(PreviewBody)가 먼저 분기 — 도달하지 않는다.
    default:
      return assertNever(doc.kind);
  }
}

/**
 * ChatDocPreviewPanel (chat-doc-preview — 2026-06-30, formats 확장 2026-07-01, resize 2026-07-01) —
 * 대화 영역 우측에서 밀려 들어오는 문서 미리보기 패널.
 *
 * markdown 은 원문을 fetch 해 renderMessageMarkdown(표·GFM·Mermaid)으로 렌더. pdf/docx/xlsx/pptx 는
 * 동일 출처 presigned URL 에서 바이트를 fetch 해 클라 렌더러(pdfjs/docx-preview/SheetJS/pptx-renderer)로 그린다.
 * 좌측 경계 드래그로 가로 폭 조절(chatPreviewStore.width, localStorage 영속).
 */
export function ChatDocPreviewPanel({ roomId }: { roomId: string }) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const open = useChatPreviewStore((s) => s.open);
  const closePreview = useChatPreviewStore((s) => s.closePreview);
  const width = useChatPreviewStore((s) => s.width);
  const setWidth = useChatPreviewStore((s) => s.setWidth);
  const fullscreen = useChatPreviewStore((s) => s.fullscreen);
  const toggleFullscreen = useChatPreviewStore((s) => s.toggleFullscreen);
  const setFullscreen = useChatPreviewStore((s) => s.setFullscreen);
  // chat-doc-preview-cols (2026-08-11) — 한 줄에 1/2/4장.
  const cols = useChatPreviewStore((s) => s.cols);
  const setCols = useChatPreviewStore((s) => s.setCols);
  // chat-doc-preview-rail (2026-08-11) — 확대/축소(자동 맞춤 대비 %).
  const zoom = useChatPreviewStore((s) => s.zoom);
  // 도크 상태에서는 배율 조작 수단이 없으므로 화면에는 항상 100% 를 먹인다(저장값은 보존).
  const appliedZoom = useEffectivePreviewZoom();
  const stepZoom = useChatPreviewStore((s) => s.stepZoom);
  const resetZoom = useChatPreviewStore((s) => s.resetZoom);
  const active = open && open.roomId === roomId ? open : null;

  // chat-doc-preview-fullscreen (2026-07-02) — 전체보기 중 Esc → 도크 상태로 복귀(패널은 유지).
  useEffect(() => {
    if (!active || !fullscreen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, fullscreen, setFullscreen]);

  // chat-doc-preview-resize (2026-07-01) — 좌측 경계 드래그로 가로 폭 조절. 패널이 우측이라
  // 왼쪽으로 끌수록 넓어짐(startX - clientX). 드래그 중엔 width transition 억제(body 클래스).
  const onResizeStart = useCallback(
    (e: ReactMouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startW = width;
      document.body.classList.add('chat-doc-preview-resizing');
      const onMove = (ev: MouseEvent) => setWidth(startW + (startX - ev.clientX));
      const onUp = () => {
        document.body.classList.remove('chat-doc-preview-resizing');
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [width, setWidth],
  );

  return (
    <aside
      className={`chat-doc-preview-panel${active ? ' is-open' : ''}${active && fullscreen ? ' is-fullscreen' : ''}`}
      style={
        {
          ['--doc-preview-width']: `${width}px`,
          // pdf 는 CSS 로 배율을 먹인다(canvas 상한에 곱). pptx 는 렌더러 setZoom 이 담당.
          ['--doc-preview-zoom']: String(appliedZoom / 100),
        } as CSSProperties
      }
      aria-hidden={!active}
      aria-label={tChat('chat.preview.aria')}
    >
      {active && (
        <div className="chat-doc-preview-inner">
          {/* 전체보기 중에는 폭 조절 핸들 숨김(전체화면이라 무의미). */}
          {!fullscreen && (
            <div
              className="chat-doc-preview-resize-handle"
              role="separator"
              aria-orientation="vertical"
              aria-label={tChat('chat.preview.resize')}
              onMouseDown={onResizeStart}
            />
          )}
          <PreviewBody
            key={active.doc.key}
            doc={active.doc}
            onClose={closePreview}
            fullscreen={fullscreen}
            onToggleFullscreen={toggleFullscreen}
            cols={cols}
            onSetCols={setCols}
            zoom={zoom}
            onStepZoom={stepZoom}
            onResetZoom={resetZoom}
          />
        </div>
      )}
    </aside>
  );
}

/**
 * 한 줄 페이지 수 선택기 (chat-doc-preview-cols — 2026-08-11, 팝업 전환 2026-08-11).
 *
 * 버튼 하나를 누르면 팝업이 열리고 그 안에서 1/2/4장을 고른다(헤더 폭 절약 + 현재 값이 한눈에).
 * 바깥 클릭·Esc 로 닫는 방식은 ChatArea Header 메뉴와 동일한 패턴.
 */
function ColsMenu({ cols, onSetCols }: { cols: number; onSetCols: (v: number) => void }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      // 전체보기 Esc(도크 복귀)보다 먼저 삼킨다 — 팝업이 열려 있으면 팝업만 닫는다.
      if (e.key === 'Escape') {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  return (
    <div className="chat-doc-preview-menu-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`chat-doc-preview-cols-trigger${open ? ' is-active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={tChat('chat.preview.cols')}
        title={tChat('chat.preview.cols')}
      >
        <LayoutGrid size={14} />
        <span className="chat-doc-preview-cols-trigger-label">{cols}</span>
      </button>
      {open && (
        <div className="chat-doc-preview-menu" role="menu" aria-label={tChat('chat.preview.cols')}>
          {CHAT_PREVIEW_COLS_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              role="menuitemradio"
              aria-checked={cols === n}
              className={`chat-doc-preview-menu-item${cols === n ? ' is-active' : ''}`}
              onClick={() => {
                onSetCols(n);
                setOpen(false);
              }}
            >
              <span className="chat-doc-preview-menu-item-check" aria-hidden>
                {cols === n ? <Check size={13} /> : null}
              </span>
              {tChat('chat.preview.cols_n', { n })}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * 확대/축소 (chat-doc-preview-rail — 2026-08-11).
 *
 * 100% = '한 장이 화면에 들어오는 자동 맞춤' 기준이고, 여기서 더 키우거나 줄인다. 숫자를 누르면
 * 100%(화면 맞춤)로 복귀. 실제 배율 적용은 pdf=CSS 변수, pptx=렌더러 setZoom 이 담당한다.
 */
function ZoomControl({
  zoom,
  onStep,
  onReset,
}: {
  zoom: number;
  onStep: (dir: 1 | -1) => void;
  onReset: () => void;
}) {
  return (
    <div className="chat-doc-preview-zoom" role="group" aria-label={tChat('chat.preview.zoom')}>
      <button
        type="button"
        className="chat-doc-preview-zoom-btn"
        onClick={() => onStep(-1)}
        disabled={zoom <= PREVIEW_ZOOM_MIN}
        aria-label={tChat('chat.preview.zoom_out')}
        title={tChat('chat.preview.zoom_out')}
      >
        <Minus size={13} />
      </button>
      <button
        type="button"
        className="chat-doc-preview-zoom-value"
        onClick={onReset}
        aria-label={tChat('chat.preview.zoom_reset')}
        title={tChat('chat.preview.zoom_reset')}
      >
        {zoom}%
      </button>
      <button
        type="button"
        className="chat-doc-preview-zoom-btn"
        onClick={() => onStep(1)}
        disabled={zoom >= PREVIEW_ZOOM_MAX}
        aria-label={tChat('chat.preview.zoom_in')}
        title={tChat('chat.preview.zoom_in')}
      >
        <Plus size={13} />
      </button>
    </div>
  );
}

/** 페이지를 여러 장 나열하는 포맷만 '한 줄 N장'·확대/축소·썸네일 레일이 의미를 갖는다. */
function isPagedKind(kind: PreviewDoc['kind']): boolean {
  return kind === 'pdf' || kind === 'pptx';
}

/**
 * 보기옵션 노출 조건 (2026-08-12 사용자 요청) — 페이지 포맷 **AND 전체보기**.
 *
 * 좁은 도크 패널에서는 한 줄 2·4장이나 확대/축소·썸네일 레일이 본문을 잡아먹기만 한다.
 * 전체보기에서만 헤더의 한 장/두 장/세 장 선택 · 확대 · 좌측 썸네일 레일을 노출한다.
 */
function showViewOptions(kind: PreviewDoc['kind'], fullscreen: boolean): boolean {
  return isPagedKind(kind) && fullscreen;
}

function PreviewBody({
  doc,
  onClose,
  fullscreen,
  onToggleFullscreen,
  cols,
  onSetCols,
  zoom,
  onStepZoom,
  onResetZoom,
}: {
  doc: PreviewDoc;
  onClose: () => void;
  fullscreen: boolean;
  onToggleFullscreen: () => void;
  cols: number;
  onSetCols: (v: number) => void;
  zoom: number;
  onStepZoom: (dir: 1 | -1) => void;
  onResetZoom: () => void;
}) {
  const sourceUrl = doc.source.kind === 'url' ? doc.source.url : null;
  const KindIcon = previewKindIcon(doc.kind);
  const fsLabel = fullscreen ? tChat('chat.preview.fullscreen_exit') : tChat('chat.preview.fullscreen');
  const paged = showViewOptions(doc.kind, fullscreen);

  return (
    <>
      <header className="chat-doc-preview-header">
        <span className="chat-doc-preview-header-icon" aria-hidden>
          <KindIcon size={15} className={previewKindClass(doc.kind)} />
        </span>
        <span className="chat-doc-preview-header-title" title={doc.title}>
          {doc.title}
        </span>
        {sourceUrl && (
          <a
            className="chat-doc-preview-header-action"
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer nofollow"
            aria-label={tChat('chat.preview.open_source')}
            title={tChat('chat.preview.open_source')}
          >
            <ExternalLink size={14} />
          </a>
        )}
        {/* 페이지 목록 포맷(pdf·pptx) + 전체보기일 때만 열 선택·확대/축소 — 그 외에는 숨긴다. */}
        {paged && <ColsMenu cols={cols} onSetCols={onSetCols} />}
        {paged && <ZoomControl zoom={zoom} onStep={onStepZoom} onReset={onResetZoom} />}
        <button
          type="button"
          className={`chat-doc-preview-header-action${fullscreen ? ' is-active' : ''}`}
          onClick={onToggleFullscreen}
          aria-label={fsLabel}
          aria-pressed={fullscreen}
          title={fsLabel}
        >
          <Maximize2 size={15} />
        </button>
        <button
          type="button"
          className="chat-doc-preview-header-action"
          onClick={onClose}
          aria-label={tChat('chat.preview.close_aria')}
          title={tChat('chat.preview.close_aria')}
        >
          <X size={16} />
        </button>
      </header>

      <div className="chat-doc-preview-main">
        {/* 썸네일 레일 — 전체보기 + 페이지가 2장 이상일 때만(장수는 레일 내부에서 판단). */}
        {paged && <PreviewThumbRail />}
        <div
          className={`chat-doc-preview-body scrollbar-thin${doc.kind === 'html' ? ' chat-doc-preview-body--html' : ''}`}
          data-cols={paged ? cols : 1}
        >
          {doc.kind === 'markdown' ? (
            <MarkdownContent doc={doc} />
          ) : doc.kind === 'html' ? (
            <HtmlContent doc={doc} />
          ) : (
            <Suspense fallback={<div className="chat-doc-preview-status" role="status">{tChat('chat.preview.loading')}</div>}>
              <OfficeContent doc={doc} />
            </Suspense>
          )}
        </div>
      </div>
    </>
  );
}

interface BodyState {
  loading: boolean;
  text: string | null;
  error: string | null;
}

/** 마크다운 원문 fetch + 렌더 (기존 로직 보존). */
function MarkdownContent({ doc }: { doc: PreviewDoc }) {
  const [state, setState] = useState<BodyState>({ loading: true, text: null, error: null });
  const sourceUrl = doc.source.kind === 'url' ? doc.source.url : null;

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, text: null, error: null });
    fetchMarkdownText(doc)
      .then((t) => {
        if (!cancelled) setState({ loading: false, text: t, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({ loading: false, text: null, error: e instanceof Error ? e.message : 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc.key]);

  if (state.loading) {
    return <div className="chat-doc-preview-status" role="status">{tChat('chat.preview.loading')}</div>;
  }
  if (state.error) {
    return (
      <div className="chat-doc-preview-status chat-doc-preview-status--error" role="status">
        <span>{tChat('chat.preview.error')}</span>
        <span className="chat-doc-preview-status-detail">{state.error}</span>
        {sourceUrl && (
          <a href={sourceUrl} target="_blank" rel="noopener noreferrer nofollow" className="chat-doc-preview-status-link">
            {tChat('chat.preview.open_source')}
          </a>
        )}
      </div>
    );
  }
  if (state.text && state.text.trim()) {
    return <div className="chat-doc-preview-md">{renderMessageMarkdown(state.text)}</div>;
  }
  return <div className="chat-doc-preview-status" role="status">{tChat('chat.preview.empty')}</div>;
}

/**
 * html 미리보기 (chat-html-preview — 2026-07-18, iOS #230 / AOS #231 웹 파리티).
 *
 * 사용자 업로드 임의 HTML 을 **sandboxed iframe** 으로 정적 렌더한다. 네이티브(WebView JS off +
 * baseURL nil + blockNetworkLoads) 등가:
 *   - `sandbox=""` (allow-scripts·allow-same-origin 미부여) → **스크립트 미실행**(stored-XSS 무력화)
 *     + opaque origin(부모 페이지/쿠키 접근 불가) + 폼/네비게이션/팝업 차단.
 *   - CSP meta(`default-src 'none'`) 선주입 → 외부 리소스/추적 픽셀 네트워크 로드 차단(인라인 style·
 *     data: 이미지만 허용). 다중 CSP 는 교집합 적용이라 문서 자체 CSP 로 느슨해지지 않는다.
 *   - `referrerPolicy="no-referrer"`.
 */
const HTML_PREVIEW_CSP =
  '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; style-src \'unsafe-inline\'; img-src data:; font-src data:; media-src data:">';

function HtmlContent({ doc }: { doc: PreviewDoc }) {
  const [state, setState] = useState<BodyState>({ loading: true, text: null, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, text: null, error: null });
    const fileId = doc.source.kind === 'attachment' ? doc.source.fileId : null;
    if (!fileId) {
      setState({ loading: false, text: null, error: 'unsupported' });
      return;
    }
    fetchAttachmentText(fileId)
      .then((t) => {
        if (!cancelled) setState({ loading: false, text: t, error: null });
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setState({ loading: false, text: null, error: e instanceof Error ? e.message : 'error' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc.key]);

  if (state.loading) {
    return <div className="chat-doc-preview-status" role="status">{tChat('chat.preview.loading')}</div>;
  }
  if (state.error) {
    return (
      <div className="chat-doc-preview-status chat-doc-preview-status--error" role="status">
        <span>{tChat('chat.preview.error')}</span>
        <span className="chat-doc-preview-status-detail">{state.error}</span>
      </div>
    );
  }
  if (state.text != null) {
    return (
      <iframe
        title={doc.title}
        className="chat-doc-preview-html-frame"
        sandbox=""
        referrerPolicy="no-referrer"
        srcDoc={HTML_PREVIEW_CSP + state.text}
      />
    );
  }
  return <div className="chat-doc-preview-status" role="status">{tChat('chat.preview.empty')}</div>;
}
